<template>
  <div class="map-overlays">
    <!-- Bouton d'ouverture -->
    <a-tooltip title="Fonds de carte (IGN)" placement="left">
      <button
        type="button"
        class="overlays-toggle"
        :class="{ active: open || overlays.active.length }"
        @click="open = !open"
      >
        <span class="overlays-toggle-icon">🗺</span>
        <span v-if="overlays.active.length" class="overlays-badge">{{ overlays.active.length }}</span>
      </button>
    </a-tooltip>

    <!-- Panneau -->
    <div v-if="open" class="overlays-panel">
      <div class="overlays-header">
        <span>Fonds de carte additionnels</span>
        <a class="overlays-close" @click="open = false"><CloseOutlined /></a>
      </div>

      <!-- Couches actives (ordre = affichage, glisser pour réordonner) -->
      <div class="overlays-section-title">
        Couches actives
        <span class="overlays-hint">— glisser pour réordonner</span>
      </div>
      <div v-if="!overlays.active.length" class="overlays-empty">
        Aucun overlay. Ajoutez-en ci-dessous.
      </div>
      <div v-else class="overlays-list">
        <div
          v-for="(o, idx) in overlays.active"
          :key="o.key"
          class="overlay-item"
          :class="{ dragging: dragIndex === idx, 'drag-over': dragOverIndex === idx }"
          draggable="true"
          @dragstart="onDragStart(idx, $event)"
          @dragover.prevent="onDragOver(idx)"
          @drop="onDrop(idx)"
          @dragend="onDragEnd"
        >
          <div class="overlay-item-head">
            <span class="overlay-handle" title="Glisser pour réordonner">⠿</span>
            <span class="overlay-name" :title="defOf(o.key)?.description">{{ defOf(o.key)?.name }}</span>
            <a
              class="overlay-eye"
              :class="{ off: !o.visible }"
              :title="o.visible ? 'Masquer' : 'Afficher'"
              @click="toggleVisible(o.key)"
            >
              <EyeOutlined v-if="o.visible" />
              <EyeInvisibleOutlined v-else />
            </a>
            <a class="overlay-remove" title="Retirer" @click="removeOverlay(o.key)"><DeleteOutlined /></a>
          </div>
          <div class="overlay-item-opacity">
            <span class="opacity-label">Opacité</span>
            <a-slider
              class="opacity-slider"
              :value="Math.round(o.opacity * 100)"
              :min="0"
              :max="100"
              :step="5"
              :disabled="!o.visible"
              :tip-formatter="(v) => `${v} %`"
              @change="(v) => setOpacity(o.key, v / 100)"
            />
            <span class="opacity-value">{{ Math.round(o.opacity * 100) }} %</span>
          </div>
        </div>
      </div>

      <!-- Catalogue : ajout -->
      <div class="overlays-section-title mt8">Ajouter une couche</div>
      <div class="overlays-catalog">
        <a-tooltip v-for="def in availableCatalog" :key="def.key" :title="def.description" placement="left">
          <button type="button" class="catalog-item" @click="addOverlay(def.key)">
            <PlusOutlined class="catalog-plus" />
            <span>{{ def.name }}</span>
          </button>
        </a-tooltip>
        <div v-if="!availableCatalog.length" class="overlays-empty">Toutes les couches sont actives.</div>
      </div>
      <div class="overlays-credit">Données : IGN — Géoplateforme (data.geopf.fr)</div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue'
import {
  CloseOutlined, DeleteOutlined, PlusOutlined, EyeOutlined, EyeInvisibleOutlined,
} from '@ant-design/icons-vue'
import { useMapOverlays } from '/@/hooks/use-map-overlays'

export default defineComponent({
  name: 'MapOverlaysPanel',
  components: { CloseOutlined, DeleteOutlined, PlusOutlined, EyeOutlined, EyeInvisibleOutlined },
  setup () {
    const {
      overlays, catalog, isActive, addOverlay, removeOverlay, setOpacity, toggleVisible, moveOverlay, getOverlayDef,
    } = useMapOverlays()

    const open = ref(false)
    const availableCatalog = computed(() => catalog.filter(d => !isActive(d.key)))
    const defOf = (key: string) => getOverlayDef(key)

    // Drag & drop natif (pas de dépendance ajoutée)
    const dragIndex = ref<number | null>(null)
    const dragOverIndex = ref<number | null>(null)

    function onDragStart (idx: number, e: DragEvent) {
      dragIndex.value = idx
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', String(idx)) // requis sur Firefox
      }
    }
    function onDragOver (idx: number) {
      dragOverIndex.value = idx
    }
    function onDrop (idx: number) {
      if (dragIndex.value !== null && dragIndex.value !== idx) {
        moveOverlay(dragIndex.value, idx)
      }
      onDragEnd()
    }
    function onDragEnd () {
      dragIndex.value = null
      dragOverIndex.value = null
    }

    return {
      overlays,
      open,
      availableCatalog,
      defOf,
      addOverlay,
      removeOverlay,
      setOpacity,
      toggleVisible,
      dragIndex,
      dragOverIndex,
      onDragStart,
      onDragOver,
      onDrop,
      onDragEnd,
    }
  },
})
</script>

<style lang="scss" scoped>
.map-overlays {
  position: absolute;
  top: 16px;
  right: 52px; // à gauche du panneau de dessin (right:16px, largeur 28px)
  z-index: 11;
}

.overlays-toggle {
  position: relative;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 2px;
  background: #fff;
  color: #2d8cf0;
  font-size: 16px;
  line-height: 28px;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);

  &:hover { box-shadow: 0 0 0 1px #2d8cf0; }
  &.active { background: #2d8cf0; color: #fff; }

  .overlays-badge {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 15px;
    height: 15px;
    padding: 0 3px;
    border-radius: 8px;
    background: #f5222d;
    color: #fff;
    font-size: 10px;
    line-height: 15px;
    text-align: center;
  }
}

.overlays-panel {
  position: absolute;
  top: 0;
  right: 34px;
  width: 268px;
  max-height: 70vh;
  overflow-y: auto;
  background: #1f1f1f;
  border: 1px solid #3a3a3a;
  border-radius: 4px;
  padding: 10px;
  color: #ddd;
  font-size: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
}

.overlays-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 8px;

  .overlays-close { color: #999; &:hover { color: #fff; } }
}

.overlays-section-title {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #8c8c8c;
  margin-bottom: 6px;

  &.mt8 { margin-top: 12px; }
  .overlays-hint { text-transform: none; letter-spacing: 0; color: #666; }
}

.overlays-empty {
  color: #777;
  font-style: italic;
  padding: 4px 0;
}

.overlay-item {
  background: #2a2a2a;
  border: 1px solid #3a3a3a;
  border-radius: 3px;
  padding: 6px 8px;
  margin-bottom: 6px;
  transition: border-color 0.15s, opacity 0.15s;

  &.dragging { opacity: 0.45; }
  &.drag-over { border-color: #2d8cf0; }
}

.overlay-item-head {
  display: flex;
  align-items: center;
  gap: 6px;

  .overlay-handle {
    cursor: grab;
    color: #777;
    font-size: 13px;
    &:active { cursor: grabbing; }
  }
  .overlay-name {
    flex: 1;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .overlay-eye { color: #bbb; &:hover { color: #fff; } &.off { color: #666; } }
  .overlay-remove { color: #b04; &:hover { color: #f5222d; } }
}

.overlay-item-opacity {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;

  .opacity-label { color: #999; white-space: nowrap; }
  .opacity-slider { flex: 1; margin: 0; }
  .opacity-value { color: #ccc; width: 34px; text-align: right; }
}

.overlays-catalog {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.catalog-item {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  text-align: left;
  background: #2a2a2a;
  border: 1px solid #3a3a3a;
  border-radius: 3px;
  color: #ddd;
  padding: 5px 8px;
  cursor: pointer;
  font-size: 12px;

  &:hover { border-color: #2d8cf0; color: #fff; }
  .catalog-plus { color: #2d8cf0; }
}

.overlays-credit {
  margin-top: 10px;
  color: #666;
  font-size: 10px;
  text-align: center;
}

// Slider antd compact en thème sombre
.opacity-slider:deep(.ant-slider-rail) { background: #444; }
.opacity-slider:deep(.ant-slider-track) { background: #2d8cf0; }
.opacity-slider:deep(.ant-slider-handle) { border-color: #2d8cf0; }
</style>
